import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Tag,
    Typography
} from 'antd';
import { EditOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import ContractBillingDocuments from './ContractBillingDocuments';
import ContractDocuments from './ContractDocuments';
import {
    getOrganizationManagementContract,
    getPlans,
    postContract,
    putOrganizationManagementContract
} from '../store/user/thunks';

const { Text } = Typography;
type AnyRecord = Record<string, any>;

interface OrganizationContractManagementProps {
    organizationId: string;
    organizationName?: string;
    getOrganizationManagementContract?: any;
    getPlans?: any;
    postContract?: any;
    putOrganizationManagementContract?: any;
}

const pick = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const formatDate = (value?: string | null) => {
    if (!value) return 'No disponible';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('es-EC');
};

const dateInputValue = (value?: string | null) => value ? String(value).slice(0, 10) : '';

const todayInput = () => new Date().toISOString().slice(0, 10);

const oneYearFromTodayInput = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 10);
};

const OrganizationContractManagement: React.FC<OrganizationContractManagementProps> = props => {
    const {
        organizationId,
        getOrganizationManagementContract,
        getPlans
    } = props;
    const [form] = Form.useForm();
    const [contract, setContract] = useState<AnyRecord | null>(null);
    const [plans, setPlans] = useState<AnyRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm] = Form.useForm();
    const [transitioning, setTransitioning] = useState(false);
    const [transitionOpen, setTransitionOpen] = useState(false);
    const [transitionForm] = Form.useForm();

    const selectedPlanId = Form.useWatch('planId', form);
    const selectedContractType = Form.useWatch('contractType', form) || 'ONLINE';
    const selectedRoleIds = (Form.useWatch('roleIds', form) || []) as string[];
    const selectedPlan = plans.find(plan => String(pick(plan, 'id', 'Id')) === String(selectedPlanId));

    const loadContract = useCallback(async () => {
        setLoading(true);
        try {
            const [response, availablePlans] = await Promise.all([
                getOrganizationManagementContract?.(organizationId),
                getPlans?.()
            ]);
            setPlans(Array.isArray(availablePlans) ? availablePlans : []);
            const currentContract = response && !Array.isArray(response) ? response as AnyRecord : null;
            setContract(currentContract);
            if (currentContract) {
                const contractRoles = (pick(currentContract, 'rolSystems', 'RolSystems') || []) as AnyRecord[];
                const roleConfigs = contractRoles.reduce((result: AnyRecord, role: AnyRecord) => {
                    const roleId = pick(role, 'rolSystemId', 'RolSystemId');
                    if (roleId) {
                        result[String(roleId)] = {
                            maxUsers: pick(role, 'maxUsers', 'MaxUsers'),
                            includedUsers: Number(pick(role, 'includedUsers', 'IncludedUsers') || 0),
                            additionalLicensePrice: Number(pick(role, 'additionalLicensePrice', 'AdditionalLicensePrice') || 0)
                        };
                    }
                    return result;
                }, {});
                const contractServiceIds = ((pick(currentContract, 'services', 'Services') || []) as AnyRecord[])
                    .map(service => pick(service, 'id', 'Id'))
                    .filter(Boolean);
                const contractType = String(pick(currentContract, 'contractType', 'ContractType') || 'ONLINE').toUpperCase();
                form.setFieldsValue({
                    planId: contractType === 'COMERCIAL' ? null : pick(currentContract, 'planId', 'PlanId'),
                    roleIds: contractRoles
                        .map(role => pick(role, 'rolSystemId', 'RolSystemId'))
                        .filter(Boolean),
                    roleConfigs,
                    serviceIds: contractServiceIds,
                    contractType,
                    billingEmail: pick(currentContract, 'billingEmail', 'BillingEmail') || '',
                    invoiceDayOfMonth: pick(currentContract, 'invoiceDayOfMonth', 'InvoiceDayOfMonth'),
                    paymentTermDays: pick(currentContract, 'paymentTermDays', 'PaymentTermDays'),
                    moraGraceDays: pick(currentContract, 'moraGraceDays', 'MoraGraceDays'),
                    paymentVerificationMode: pick(currentContract, 'paymentVerificationMode', 'PaymentVerificationMode') || 'MANUAL',
                    agreedPrice: Number(pick(currentContract, 'agreedPrice', 'AgreedPrice') || 0),
                    discountPercentage: pick(currentContract, 'discountPercentage', 'DiscountPercentage'),
                    autoRenew: Boolean(pick(currentContract, 'autoRenew', 'AutoRenew')),
                    renewalPeriodMonths: pick(currentContract, 'renewalPeriodMonths', 'RenewalPeriodMonths'),
                    endDate: dateInputValue(pick(currentContract, 'endDate', 'EndDate')),
                    status: pick(currentContract, 'status', 'Status') || 'ACTIVO',
                    signedAt: dateInputValue(pick(currentContract, 'signedAt', 'SignedAt')),
                    signedBy: pick(currentContract, 'signedBy', 'SignedBy') || '',
                    termsConditions: pick(currentContract, 'termsConditions', 'TermsConditions') || '',
                    notes: pick(currentContract, 'notes', 'Notes') || ''
                });
            } else {
                form.resetFields();
            }
        } finally {
            setLoading(false);
        }
    }, [form, getOrganizationManagementContract, getPlans, organizationId]);

    useEffect(() => {
        loadContract();
    }, [loadContract]);

    const handlePlanChange = (planId: string) => {
        const plan = plans.find(item => String(pick(item, 'id', 'Id')) === String(planId));
        if (!plan) return;

        const planRoles = (pick(plan, 'rolSystems', 'RolSystems') || []) as AnyRecord[];
        const roleConfigs = planRoles.reduce((result: AnyRecord, role: AnyRecord) => {
            const roleId = pick(role, 'rolSystemId', 'RolSystemId');
            if (roleId) {
                result[String(roleId)] = {
                    maxUsers: pick(role, 'maxUsers', 'MaxUsers'),
                    includedUsers: Number(pick(role, 'includedUsers', 'IncludedUsers') || 0),
                    additionalLicensePrice: Number(pick(role, 'additionalLicensePrice', 'AdditionalLicensePrice') || 0)
                };
            }
            return result;
        }, {});

        form.setFieldsValue({
            roleIds: planRoles.map(role => pick(role, 'rolSystemId', 'RolSystemId')).filter(Boolean),
            roleConfigs,
            serviceIds: ((pick(plan, 'services', 'Services') || []) as AnyRecord[])
                .map(service => pick(service, 'id', 'Id'))
                .filter(Boolean)
        });
    };

    const saveContract = async () => {
        if (!contract) return;
        const values = await form.validateFields();
        setSaving(true);
        try {
            const signedAt = values.signedAt ? `${values.signedAt}T00:00:00.000Z` : null;
            const endDate = values.endDate ? `${values.endDate}T23:59:59.999Z` : null;
            const roleConfigs = values.roleConfigs || {};
            const roleSystems = (values.roleIds || []).map((roleId: string) => ({
                rolSystemId: roleId,
                maxUsers: roleConfigs[roleId]?.maxUsers === undefined || roleConfigs[roleId]?.maxUsers === null
                    ? null
                    : Number(roleConfigs[roleId].maxUsers),
                includedUsers: Number(roleConfigs[roleId]?.includedUsers || 0),
                additionalLicensePrice: Number(roleConfigs[roleId]?.additionalLicensePrice || 0)
            }));
            const result = await props.putOrganizationManagementContract?.(props.organizationId, {
                id: pick(contract, 'id', 'Id'),
                organizationId: props.organizationId,
                planId: String(values.contractType || 'ONLINE').toUpperCase() === 'COMERCIAL'
                    ? null
                    : (values.planId || pick(contract, 'planId', 'PlanId')),
                contractType: values.contractType || 'ONLINE',
                startDate: pick(contract, 'startDate', 'StartDate'),
                endDate,
                status: values.status,
                rolSystems: roleSystems,
                serviceIds: values.serviceIds || [],
                billingEmail: values.billingEmail || null,
                agreedPrice: Number(values.agreedPrice || 0),
                discountPercentage: values.discountPercentage === undefined || values.discountPercentage === null
                    ? null
                    : Number(values.discountPercentage),
                autoRenew: Boolean(values.autoRenew),
                renewalPeriodMonths: values.renewalPeriodMonths
                    ? Number(values.renewalPeriodMonths)
                    : null,
                signedAt,
                signedBy: values.signedBy || null,
                terminationDate: pick(contract, 'terminationDate', 'TerminationDate') || null,
                termsConditions: pick(contract, 'termsConditions', 'TermsConditions') || null,
                notes: values.notes || null,
                ...(String(values.contractType || 'ONLINE').toUpperCase() === 'COMERCIAL' ? {
                    invoiceDayOfMonth: values.invoiceDayOfMonth ?? null,
                    paymentTermDays: values.paymentTermDays ?? null,
                    moraGraceDays: values.moraGraceDays ?? null,
                    paymentVerificationMode: values.paymentVerificationMode || 'MANUAL'
                } : {}),
                previousContractId: pick(contract, 'previousContractId', 'PreviousContractId') || null
            });

            if (result) {
                await loadContract();
            }
        } finally {
            setSaving(false);
        }
    };

    const openCommercialContract = () => {
        createForm.setFieldsValue({
            startDate: todayInput(),
            endDate: oneYearFromTodayInput(),
            signedAt: todayInput(),
            discountPercentage: null,
            paymentVerificationMode: 'MANUAL',
            autoRenew: false
        });
        setCreateOpen(true);
    };

    const createCommercialContract = async (values: AnyRecord) => {
        const startDate = values.startDate ? `${values.startDate}T00:00:00.000Z` : null;
        const endDate = values.endDate ? `${values.endDate}T23:59:59.999Z` : null;
        if (startDate && endDate && new Date(endDate).getTime() <= new Date(startDate).getTime()) {
            createForm.setFields([{ name: 'endDate', errors: ['La fecha de fin debe ser posterior a la fecha de inicio.'] }]);
            return;
        }

        setCreating(true);
        try {
            const result = await props.postContract?.({
                organizationId: props.organizationId,
                planId: null,
                contractType: 'COMERCIAL',
                startDate,
                endDate,
                agreedPrice: Number(values.agreedPrice || 0),
                discountPercentage: values.discountPercentage === undefined || values.discountPercentage === null
                    ? null
                    : Number(values.discountPercentage),
                invoiceDayOfMonth: values.invoiceDayOfMonth ?? null,
                paymentTermDays: values.paymentTermDays ?? null,
                moraGraceDays: values.moraGraceDays ?? null,
                paymentVerificationMode: values.paymentVerificationMode || 'MANUAL',
                autoRenew: false,
                renewalPeriodMonths: null,
                billingEmail: values.billingEmail || null,
                signedAt: values.signedAt ? `${values.signedAt}T00:00:00.000Z` : null,
                signedBy: values.signedBy || null,
                termsConditions: null,
                notes: values.notes || null,
                rolSystems: null,
                serviceIds: null
            });

            if (result) {
                setCreateOpen(false);
                createForm.resetFields();
                await loadContract();
            }
        } finally {
            setCreating(false);
        }
    };

    const openCommercialTransition = () => {
        const currentType = String(pick(contract, 'contractType', 'ContractType') || 'ONLINE').toUpperCase();
        if (!contract || currentType === 'COMERCIAL') return;

        const today = todayInput();
        const currentEndDate = dateInputValue(pick(contract, 'endDate', 'EndDate'));
        const defaultEndDate = currentEndDate && currentEndDate > today
            ? currentEndDate
            : oneYearFromTodayInput();

        transitionForm.setFieldsValue({
            startDate: todayInput(),
            endDate: defaultEndDate,
            agreedPrice: Number(pick(contract, 'agreedPrice', 'AgreedPrice') || 0),
            discountPercentage: pick(contract, 'discountPercentage', 'DiscountPercentage'),
            invoiceDayOfMonth: pick(contract, 'invoiceDayOfMonth', 'InvoiceDayOfMonth'),
            paymentTermDays: pick(contract, 'paymentTermDays', 'PaymentTermDays'),
            moraGraceDays: pick(contract, 'moraGraceDays', 'MoraGraceDays'),
            paymentVerificationMode: pick(contract, 'paymentVerificationMode', 'PaymentVerificationMode') || 'MANUAL',
            billingEmail: pick(contract, 'billingEmail', 'BillingEmail') || '',
            signedAt: todayInput(),
            signedBy: '',
            notes: `Conversión desde contrato #${pick(contract, 'contractNumber', 'ContractNumber')}`
        });
        setTransitionOpen(true);
    };

    const transitionToCommercial = async (values: AnyRecord) => {
        if (!contract) return;

        const startDate = values.startDate ? `${values.startDate}T00:00:00.000Z` : null;
        const endDate = values.endDate ? `${values.endDate}T23:59:59.999Z` : null;
        if (startDate && endDate && new Date(endDate).getTime() <= new Date(startDate).getTime()) {
            transitionForm.setFields([{ name: 'endDate', errors: ['La fecha de fin debe ser posterior a la fecha de inicio.'] }]);
            return;
        }

        setTransitioning(true);
        try {
            const result = await props.postContract?.({
                organizationId: props.organizationId,
                planId: null,
                previousContractId: pick(contract, 'id', 'Id'),
                contractType: 'COMERCIAL',
                startDate,
                endDate,
                agreedPrice: Number(values.agreedPrice || 0),
                discountPercentage: values.discountPercentage === undefined || values.discountPercentage === null
                    ? null
                    : Number(values.discountPercentage),
                invoiceDayOfMonth: values.invoiceDayOfMonth ?? null,
                paymentTermDays: values.paymentTermDays ?? null,
                moraGraceDays: values.moraGraceDays ?? null,
                paymentVerificationMode: values.paymentVerificationMode || 'MANUAL',
                autoRenew: false,
                renewalPeriodMonths: null,
                billingEmail: values.billingEmail || null,
                signedAt: values.signedAt ? `${values.signedAt}T00:00:00.000Z` : null,
                signedBy: values.signedBy || null,
                termsConditions: pick(contract, 'termsConditions', 'TermsConditions') || null,
                notes: values.notes || null,
                rolSystems: ((pick(contract, 'rolSystems', 'RolSystems') || []) as AnyRecord[])
                    .map(role => ({
                        rolSystemId: pick(role, 'rolSystemId', 'RolSystemId'),
                        maxUsers: pick(role, 'maxUsers', 'MaxUsers'),
                        includedUsers: Number(pick(role, 'includedUsers', 'IncludedUsers') || 0),
                        additionalLicensePrice: Number(pick(role, 'additionalLicensePrice', 'AdditionalLicensePrice') || 0)
                    })),
                serviceIds: ((pick(contract, 'services', 'Services') || []) as AnyRecord[])
                    .map(service => pick(service, 'id', 'Id'))
                    .filter(Boolean)
            });

            if (result) {
                setTransitionOpen(false);
                transitionForm.resetFields();
                await loadContract();
            }
        } finally {
            setTransitioning(false);
        }
    };

    if (loading) return <Card><Spin /></Card>;

    if (!contract) {
        return <>
            <Card>
                <Empty description="No existe un contrato activo para esta organización" />
                <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    message="Puede registrar un contrato comercial firmado"
                    description="Esta opción es exclusiva del superadministrador. El contrato queda vigente hasta la fecha indicada y no se renueva ni se cobra automáticamente por el flujo online."
                />
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Button type="primary" icon={<EditOutlined />} onClick={openCommercialContract}>
                        Registrar contrato comercial
                    </Button>
                </div>
            </Card>

            <Modal
                open={createOpen}
                title="Registrar contrato comercial"
                width={760}
                destroyOnClose
                onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
                footer={null}
            >
                <Alert
                    type="warning"
                    showIcon
                    message="Contrato administrado por Blue"
                    description="Este contrato comercial es independiente de los planes online. Registre directamente el precio, la vigencia y las condiciones pactadas. No se activará renovación automática."
                    style={{ marginBottom: 16 }}
                />
                <Form form={createForm} layout="vertical" onFinish={createCommercialContract}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Inicio" name="startDate" rules={[{ required: true, message: 'Ingrese la fecha de inicio' }]}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Fin" name="endDate" rules={[{ required: true, message: 'Ingrese la fecha de fin' }]}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Precio acordado" name="agreedPrice" rules={[{ required: true, message: 'Ingrese el precio acordado' }]}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Descuento (%)" name="discountPercentage" rules={[{ type: 'number', min: 0, max: 100, message: 'Use un valor entre 0 y 100' }]}>
                                <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Correo de facturación" name="billingEmail" rules={[{ type: 'email', message: 'Ingrese un correo válido' }]}>
                                <Input placeholder="facturacion@cliente.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Alert
                                type="info"
                                showIcon
                                message="Configuración de facturación (opcional)"
                                description="Configure el día de envío, el plazo de pago y los días de gracia. Esta configuración no genera facturas hasta que se habilite el flujo de billing."
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Día de generación de factura" name="invoiceDayOfMonth">
                                <InputNumber min={1} max={31} precision={0} style={{ width: '100%' }} placeholder="1 a 31" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Plazo de pago (días)" name="paymentTermDays">
                                <InputNumber min={0} max={90} precision={0} style={{ width: '100%' }} placeholder="Ej. 5" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Días adicionales en mora" name="moraGraceDays">
                                <InputNumber min={0} max={30} precision={0} style={{ width: '100%' }} placeholder="Ej. 2" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Verificación del pago" name="paymentVerificationMode">
                                <Select options={[{ value: 'MANUAL', label: 'Manual por Blue' }]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Fecha de firma" name="signedAt">
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item label="Firmado por" name="signedBy">
                                <Input placeholder="Nombre de la persona responsable" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Alert
                                type="info"
                                showIcon
                                message="Documentos legales"
                                description="La asociación y aceptación de documentos legales se administra en el flujo legal de la organización. No se editan desde el contrato."
                            />
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Notas internas" name="notes">
                                <Input.TextArea rows={2} placeholder="Observaciones administrativas" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={() => { setCreateOpen(false); createForm.resetFields(); }}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" loading={creating}>Registrar contrato</Button>
                    </Space>
                </Form>
            </Modal>
        </>;
    }

    const status = String(pick(contract, 'status', 'Status') || '');
    const roleSystems = (pick(contract, 'rolSystems', 'RolSystems') || []) as AnyRecord[];
    const services = (pick(contract, 'services', 'Services') || []) as AnyRecord[];
    const availableRoles = (pick(selectedPlan, 'rolSystems', 'RolSystems') || roleSystems) as AnyRecord[];
    const availableServices = (pick(selectedPlan, 'services', 'Services') || services) as AnyRecord[];

    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
            title={<Space><EditOutlined /> Contrato de {props.organizationName || pick(contract, 'organizationName', 'OrganizationName') || 'organización'}</Space>}
            extra={<Space>
                {String(pick(contract, 'contractType', 'ContractType') || 'ONLINE').toUpperCase() !== 'COMERCIAL' && (
                    <Button type="primary" onClick={openCommercialTransition}>Cambiar a comercial</Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={loadContract}>Actualizar</Button>
            </Space>}
        >
            <Alert
                type="warning"
                showIcon
                message="Edición administrativa del contrato"
                description={selectedContractType === 'COMERCIAL'
                    ? 'El superadministrador puede modificar precio, roles, servicios, capacidades, fechas, condiciones y estado del contrato comercial. Los cambios no modifican facturas ya emitidas.'
                    : 'El superadministrador puede modificar el plan, roles, servicios, capacidades, fechas y estado. Los cambios aplican al contrato actual y a los próximos cobros; no modifican facturas ya emitidas.'}
                style={{ marginBottom: 16 }}
            />
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Contrato">{pick(contract, 'contractNumber', 'ContractNumber')}</Descriptions.Item>
                <Descriptions.Item label="Modalidad">{pick(contract, 'contractType', 'ContractType') === 'COMERCIAL' ? 'Comercial / firmado' : 'Online'}</Descriptions.Item>
                <Descriptions.Item label="Plan">{pick(contract, 'planName', 'PlanName') || (String(pick(contract, 'contractType', 'ContractType')).toUpperCase() === 'COMERCIAL' ? 'No aplica (comercial)' : 'No disponible')}</Descriptions.Item>
                <Descriptions.Item label="Estado"><Tag color={status === 'ACTIVO' ? 'green' : status === 'CANCELADO' ? 'red' : 'orange'}>{status || 'No disponible'}</Tag></Descriptions.Item>
                <Descriptions.Item label="Inicio">{formatDate(pick(contract, 'startDate', 'StartDate'))}</Descriptions.Item>
                <Descriptions.Item label="Fin">{formatDate(pick(contract, 'endDate', 'EndDate'))}</Descriptions.Item>
                <Descriptions.Item label="Ciclo">{pick(contract, 'billingCycle', 'BillingCycle') || 'No disponible'}</Descriptions.Item>
                <Descriptions.Item label="Total actual">{Number(pick(contract, 'totalPrice', 'TotalPrice') || 0).toFixed(2)} {pick(contract, 'currency', 'Currency') || 'USD'}</Descriptions.Item>
                <Descriptions.Item label="Creado por">{pick(contract, 'signedBy', 'SignedBy') || 'No registrado'}</Descriptions.Item>
            </Descriptions>
        </Card>

        {String(pick(contract, 'contractType', 'ContractType') || '').toUpperCase() === 'COMERCIAL'
            && <ContractBillingDocuments
                organizationId={organizationId}
                contractId={String(pick(contract, 'id', 'Id'))}
            />}

        <Modal
            open={transitionOpen}
            title="Cambiar a contrato comercial"
            width={760}
            destroyOnClose
            onCancel={() => { setTransitionOpen(false); transitionForm.resetFields(); }}
            footer={null}
        >
            <Alert
                type="warning"
                showIcon
                message="Se creará un nuevo contrato y se conservará el anterior"
                description={`El contrato #${pick(contract, 'contractNumber', 'ContractNumber')} se marcará como INACTIVO y el nuevo contrato COMERCIAL, independiente de planes online, quedará relacionado mediante previousContractId. Las facturas y pagos anteriores no se modifican.`}
                style={{ marginBottom: 16 }}
            />
            <Alert
                type="info"
                showIcon
                message="Contrato comercial independiente"
                description="No seleccione un plan online. Defina directamente la vigencia, precio, condiciones y responsable de la contratación. Los roles y servicios del contrato anterior se conservarán."
                style={{ marginBottom: 16 }}
            />
            <Form form={transitionForm} layout="vertical" onFinish={transitionToCommercial}>
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Inicio comercial" name="startDate" rules={[{ required: true, message: 'Ingrese la fecha de inicio' }]}>
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Fin comercial" name="endDate" rules={[{ required: true, message: 'Ingrese la fecha de fin' }]}>
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Precio acordado" name="agreedPrice" rules={[{ required: true, message: 'Ingrese el precio acordado' }]}>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Descuento (%)" name="discountPercentage" rules={[{ type: 'number', min: 0, max: 100, message: 'Use un valor entre 0 y 100' }]}>
                            <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Correo de facturación" name="billingEmail" rules={[{ type: 'email', message: 'Ingrese un correo válido' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Alert
                            type="info"
                            showIcon
                            message="Configuración de facturación (opcional)"
                            description="Configure el día de envío, el plazo de pago y los días de gracia para el nuevo contrato comercial."
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Día de generación de factura" name="invoiceDayOfMonth">
                            <InputNumber min={1} max={31} precision={0} style={{ width: '100%' }} placeholder="1 a 31" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Plazo de pago (días)" name="paymentTermDays">
                            <InputNumber min={0} max={90} precision={0} style={{ width: '100%' }} placeholder="Ej. 5" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Días adicionales en mora" name="moraGraceDays">
                            <InputNumber min={0} max={30} precision={0} style={{ width: '100%' }} placeholder="Ej. 2" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Verificación del pago" name="paymentVerificationMode">
                            <Select options={[{ value: 'MANUAL', label: 'Manual por Blue' }]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Fecha de firma" name="signedAt">
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                        <Form.Item label="Firmado por" name="signedBy">
                            <Input placeholder="Responsable de la firma" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Alert
                            type="info"
                            showIcon
                            message="Documentos legales"
                            description="La asociación y aceptación de documentos legales se administra en el flujo legal de la organización. No se editan desde el contrato."
                        />
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Notas internas" name="notes">
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Col>
                </Row>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={() => { setTransitionOpen(false); transitionForm.resetFields(); }}>Cancelar</Button>
                    <Button type="primary" htmlType="submit" loading={transitioning}>Crear contrato comercial</Button>
                </Space>
            </Form>
        </Modal>

        <Card title="Datos editables del contrato">
            <Form form={form} layout="vertical" onFinish={saveContract}>
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                        <Form.Item name="contractType" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Modalidad del contrato"
                            extra="La modalidad no se cambia editando el contrato. Use 'Cambiar a comercial' para crear una nueva versión con trazabilidad."
                        >
                            <Tag color={selectedContractType === 'COMERCIAL' ? 'purple' : 'blue'}>
                                {selectedContractType === 'COMERCIAL' ? 'Comercial / contrato firmado' : 'Online / renovación por pago'}
                            </Tag>
                        </Form.Item>
                    </Col>
                    {selectedContractType === 'ONLINE' ? <Col xs={24} md={12}>
                        <Form.Item label="Plan contratado" name="planId" rules={[{ required: true, message: 'Seleccione el plan' }]}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={plans.map(plan => ({
                                    value: pick(plan, 'id', 'Id'),
                                    label: pick(plan, 'name', 'Name') || pick(plan, 'code', 'Code') || String(pick(plan, 'id', 'Id'))
                                }))}
                                onChange={handlePlanChange}
                                placeholder="Seleccione el plan"
                            />
                        </Form.Item>
                    </Col> : <Col xs={24} md={12}>
                        <Form.Item label="Plan contratado">
                            <Alert
                                type="info"
                                showIcon
                                message="Contrato comercial independiente"
                                description="No utiliza un plan online. El precio, vigencia, roles y servicios se administran directamente en este contrato."
                            />
                        </Form.Item>
                    </Col>}
                    <Col xs={24} md={12}>
                        <Form.Item label="Correo de facturación" name="billingEmail" rules={[{ type: 'email', message: 'Ingrese un correo válido' }]}>
                            <Input placeholder="facturacion@cliente.com" />
                        </Form.Item>
                    </Col>
                    {selectedContractType === 'COMERCIAL' && <>
                        <Col xs={24}>
                            <Alert
                                type="info"
                                showIcon
                                message="Configuración de facturación (opcional)"
                                description="Si queda vacía, el contrato no tendrá una fecha automática de generación. La verificación de pago continuará siendo manual."
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Día de generación de factura" name="invoiceDayOfMonth">
                                <InputNumber min={1} max={31} precision={0} style={{ width: '100%' }} placeholder="1 a 31" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Plazo de pago (días)" name="paymentTermDays">
                                <InputNumber min={0} max={90} precision={0} style={{ width: '100%' }} placeholder="Ej. 5" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Días adicionales en mora" name="moraGraceDays">
                                <InputNumber min={0} max={30} precision={0} style={{ width: '100%' }} placeholder="Ej. 2" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Verificación del pago" name="paymentVerificationMode">
                                <Select options={[{ value: 'MANUAL', label: 'Manual por Blue' }]} />
                            </Form.Item>
                        </Col>
                    </>}
                    <Col xs={24} md={6}>
                        <Form.Item label="Precio acordado" name="agreedPrice" rules={[{ required: true, message: 'Ingrese el precio acordado' }]}>
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item label="Descuento (%)" name="discountPercentage" rules={[{ type: 'number', min: 0, max: 100, message: 'Use un valor entre 0 y 100' }]}>
                            <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} addonAfter="%" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Renovación automática" name="autoRenew" valuePropName="checked">
                            <Switch disabled={selectedContractType === 'COMERCIAL'} checkedChildren="Sí" unCheckedChildren="No" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Períodos de renovación (meses)" name="renewalPeriodMonths">
                            <InputNumber disabled={selectedContractType === 'COMERCIAL'} min={1} precision={0} style={{ width: '100%' }} placeholder="Según el ciclo" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Fecha de fin de vigencia" name="endDate" rules={[{ required: true, message: 'Ingrese la fecha de fin' }]}>
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Estado administrativo" name="status" rules={[{ required: true, message: 'Seleccione el estado' }]}>
                            <Select options={[
                                { value: 'ACTIVO', label: 'Activo' },
                                { value: 'MORA', label: 'En mora' },
                                { value: 'CANCELADO', label: 'Cancelado' },
                                { value: 'INACTIVO', label: 'Inactivo' }
                            ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Fecha de firma" name="signedAt">
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Firmado por" name="signedBy">
                            <Input placeholder="Nombre de la persona responsable" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Alert
                            type="info"
                            showIcon
                            message="Documentos legales"
                            description="La asociación y aceptación de documentos legales se administra en el flujo legal de la organización. No se editan desde el contrato."
                        />
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Notas internas" name="notes">
                            <Input.TextArea rows={3} placeholder="Observaciones administrativas" />
                        </Form.Item>
                    </Col>
                </Row>
                <Card size="small" title="Roles y capacidades del contrato" style={{ marginBottom: 16 }}>
                    <Form.Item
                        label="Roles y sistemas habilitados"
                        name="roleIds"
                        extra={selectedContractType === 'ONLINE'
                            ? 'Puede quitar roles o agregar los que estén disponibles en el plan seleccionado.'
                            : 'En un contrato comercial puede administrar directamente los roles incluidos.'}
                    >
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            options={availableRoles.map(role => ({
                                value: pick(role, 'rolSystemId', 'RolSystemId'),
                                label: `${pick(role, 'systemName', 'SystemName') || 'Sistema'} - ${pick(role, 'rolName', 'RolName') || 'Rol'}`
                            }))}
                            placeholder="Seleccione roles"
                        />
                    </Form.Item>
                    <Row gutter={[12, 12]}>
                        {selectedRoleIds.map(roleId => {
                            const role = availableRoles.find(item => String(pick(item, 'rolSystemId', 'RolSystemId')) === String(roleId));
                            return <Col xs={24} md={12} lg={8} key={roleId}>
                                <Card size="small" title={`${pick(role, 'systemName', 'SystemName') || 'Sistema'} - ${pick(role, 'rolName', 'RolName') || 'Rol'}`}>
                                    <Form.Item label="Máximo de usuarios" name={['roleConfigs', roleId, 'maxUsers']}>
                                        <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="0 = ilimitado" />
                                    </Form.Item>
                                    <Form.Item label="Licencias incluidas" name={['roleConfigs', roleId, 'includedUsers']}>
                                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item label="Precio licencia adicional" name={['roleConfigs', roleId, 'additionalLicensePrice']}>
                                        <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                                    </Form.Item>
                                </Card>
                            </Col>;
                        })}
                    </Row>
                </Card>
                <Card size="small" title="Servicios del contrato" style={{ marginBottom: 16 }}>
                    <Form.Item
                        label="Servicios habilitados"
                        name="serviceIds"
                        extra={selectedContractType === 'ONLINE'
                            ? 'La lista se limita a los servicios configurados en el plan seleccionado.'
                            : 'En un contrato comercial puede administrar directamente los servicios incluidos.'}
                    >
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            options={availableServices.map(service => ({
                                value: pick(service, 'id', 'Id'),
                                label: pick(service, 'name', 'Name') || String(pick(service, 'id', 'Id'))
                            }))}
                            placeholder="Seleccione servicios"
                        />
                    </Form.Item>
                </Card>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={saving}>
                    Guardar cambios del contrato
                </Button>
            </Form>
        </Card>

        <Card title="Resumen de roles incluidos">
            {roleSystems.length ? <Row gutter={[16, 16]}>
                {roleSystems.map(role => <Col xs={24} sm={12} md={8} key={String(pick(role, 'rolSystemId', 'RolSystemId'))}>
                    <Card size="small">
                        <Text strong>{pick(role, 'systemName', 'SystemName') || 'Sistema'} - {pick(role, 'rolName', 'RolName') || 'Rol'}</Text>
                        <br />
                        <Text type="secondary">Máximo: {pick(role, 'maxUsers', 'MaxUsers') === 0 ? 'Ilimitado' : pick(role, 'maxUsers', 'MaxUsers') ?? 'No definido'}</Text>
                        <br />
                        <Text type="secondary">Incluidas: {pick(role, 'includedUsers', 'IncludedUsers') ?? 0}</Text>
                    </Card>
                </Col>)}
            </Row> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin roles asociados" />}
        </Card>

        <Card title="Resumen de servicios del contrato">
            {services.length ? <Space wrap>{services.map(service => <Tag key={String(pick(service, 'id', 'Id'))}>{pick(service, 'name', 'Name')}</Tag>)}</Space> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin servicios asociados" />}
        </Card>

        {selectedContractType === 'COMERCIAL' && contract && <ContractDocuments
            organizationId={organizationId}
            contractId={String(pick(contract, 'id', 'Id'))}
        />}
    </Space>;
};

export default connect(null, {
    getOrganizationManagementContract,
    getPlans,
    postContract,
    putOrganizationManagementContract
})(OrganizationContractManagement);
